import type { CSSProperties } from 'react'
import type { TableProps, TablePaginationConfig } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import type { UseScrollConfig } from '../common/helper'
import type { FilterGroup } from 'src/utils/common'
import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import { autorun, reaction, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Table } from 'antd'
import { FormPropsContext, ListPropsContext } from 'src/components'
import { ListTableNative, ListTableCard, ListTableSubTable, ListTableFooter } from './components'
import { appStore, searchStore, listStore, dynamicJsStore } from 'src/stores'
import { getTableData, getTablesConfig_v1 } from 'src/components/root/tabler'
import { useTableScroll } from '../common/helper'
import { api, $api, $consts, $evt } from 'src/plugins'
import { useLatest, useIsLoading } from 'src/hooks'
import { uuid } from 'src/utils/tools'
import { set, isEqual, uniq, uniqWith, mergeWith } from 'lodash'
import 'src/styles/components/base/listTable.less'

export type ListTableProps = {
  formCode: string,
  cardMode?: boolean, // 是否以卡片形式展示列表
  cardRender?: () => React.ReactNode, // 自定义卡片渲染
  singlePageTable?: boolean, // 是否隐藏分页组件
  radioEnable?: boolean, // 单选
  checkboxEnable?: boolean, // 多选
  selectedKeys?: Array<any>, // 选中行
  onSelectChange?: (selectedKeys: string[]) => void,
  preventAutoData?: boolean, // 是否阻止默认加载数据
  filterGroup?: FilterGroup, // 额外的过滤参数
  runtimeFilterGroup?: (listTable: any) => FilterGroup, // 运行时生成的过滤参数
  onLoadedData?: Function, // 加载完数据时的回调
  parentTableId?: string, // 作为子表时，父表 id
  tableProps?: TableProps<any>, // Antd Table 属性
  useScroll?: UseScrollConfig, // useTableScroll 配置
  onRef?: (tableIns: any) => void,
  className?: string,
  style?: CSSProperties
}

const ListTableContext = React.createContext<any>(undefined)
const ListTable = (props: ListTableProps) => {
  const formProps = useContext(FormPropsContext)
  const listProps = useLatest(useContext(ListPropsContext))

  const state = useLocalObservable(() => {
    return {
      uniqClass: uuid(),
      // formCode 对应表单的表格配置
      tableConfig: {} as any,
      setTableConfig (config: any) {
        this.tableConfig = config
      },
      // 是否列表以卡片形式展示
      get cardList () {
        return props.cardMode || !!this.tableConfig.PageTable?.IS_SHOW_CARD
      },
      // 是否列表以原生table形式渲染
      get nativeTable () {
        return !!this.tableConfig.PageTable?.IS_NATIVE_TABLE
      },
      // 表单动态加载 js
      get dynamicJs () {
        const { Nav_Form } = this.tableConfig
        const { JS_FILES } = Nav_Form
        const funcs = dynamicJsStore.dynamicJs[JS_FILES?.split('.')[0]]
        return funcs || {}
      },
      // 获取 ListTable 额外请求参数
      getFilterGroup () {
        return mergeWith(
          props.runtimeFilterGroup?.(this) ?? {},
          props.filterGroup,
          listProps.current?.formData?.filterGroup,
          function (target, source) {
            if (Array.isArray(target)) {
              return target.concat(source)
            }
          }
        )
      },
      // table 数据
      tableData: { columns: [], dataSource: [] },
      setTableData (data: any) {
        Object.assign(this.tableData, data)
      },
      getTableData () {
        const searchFilterGroup = searchStore.search.get(props.formCode)?.getSearchFilterGroup()
        return getTableData({
          tableConfig: {
            ...this.tableConfig,
            __EDIT_V1__: listProps.current?.formData?.__EDIT_V1__,
            __EDIT_V2__: listProps.current?.formData?.__EDIT_V2__,
          },
          pageOption: this.pageOption,
          // ListSearch 的搜索参数
          searchFilterGroup,
          // 额外请求参数
          filterGroup: this.getFilterGroup(),
          // 页面来源
          fromWhere: formProps.formData?.fromWhere
        }).then((data: any) => {
          const { columns, dataSource, total } = data
          // 所有加载过的 dataSource
          // uniqWith 注意 concat 顺序，uniqWith 会把数组后面符合条件的给去掉，所以需要最新的在前面
          this.setLoadedData(uniqWith([].concat(dataSource, this.loadedData), (a: any, b: any) => a.ID === b.ID))
          // 表格数据
          this.setTableData({ columns, dataSource })
          // 分页数据
          total !== this.pagination.total && this.setPagination({ total })
          // 删除完一页数据，加载上一页数据
          if (!dataSource.length && total !== 0) {
            const current = this.pagination.current - 1
            this.setPagination({ current: current < 1 ? 1 : current })
          }
          // 暴露 onLoadedData 接口
          props.onLoadedData?.({ data, config: toJS(this.tableConfig) })
        })
      },
      // 分页数据
      pagination: {
        // 分页
        current: 1,
        pageSize: 10,
        total: 0,
        // 排序
        field: '',
        order: undefined
      },
      setPagination (
        page: TablePaginationConfig,
        filter?: any,
        sorter?: SorterResult<any> | SorterResult<any>[]
      ) {
        const targetSorter = Array.isArray(sorter) ? sorter[0] : sorter
        Object.assign(this.pagination, {
          ...page,
          field: targetSorter?.field ?? '',
          order: targetSorter?.order
        })
      },
      // Table 分页参数
      get paginationOption () {
        // 分页位置如果配置为 false，则不显示分页
        if (this.tableConfig.PageTable?.POSITION === 'false') return false

        const { current, pageSize, total } = this.pagination
        // 超过 3 页，则显示快捷跳转
        const shower = Math.ceil(total / pageSize) >= 3
        return {
          current,
          pageSize,
          total,
          pageSizeOptions: ['5', '10', '20'],
          showSizeChanger: shower,
          showQuickJumper: shower,
          showTotal: (total: number) => `共 ${total} 条记录`,
        }
      },
      // 分页请求参数
      get pageOption () {
        const { current: PageIndex, pageSize: Limit, field, order } = this.pagination
        return {
          PageIndex,
          Limit,
          Sort: order ? field : '',
          Order: order ? (order === 'descend' ? 1 : 0) : undefined
        }
      },
      // 所有加载过的 dataSource
      loadedData: [] as any,
      setLoadedData (data: any[]) {
        this.loadedData = data
      },
      // 当前选中项
      selectedKeys: [] as any,
      setSelectedKeys (keys: any[]) {
        this.selectedKeys = keys
      },
      resetSelectedKeys () {
        this.selectedKeys = []
      },
      // 当前选中数据，单选/多选
      get selectedData () {
        return this.loadedData.filter((item: any) => this.selectedKeys.indexOf(item.ID) !== -1)
      },
      // 单击选中的行数据
      selectedRow: {},
      setSelectedRow (record: any) {
        this.selectedRow = record
      },
      // 搜索
      search () {
        if (this.pagination.current !== 1) {
          // 重置分页参数，reaction 会自动 getTableData
          this.setPagination({ current: 1 })
        } else {
          this.getTableData().finally(() => {
            // 清空选中项
            this.resetSelectedKeys()
          })
        }
      },
      // 刷新
      reload () {
        this.getTableData()
      }
    }
  })
  useEffect(() => {
    listStore.table.set(props.formCode, state)
    // 直接通过 onRef 暴露 ListTable 的 Context
    props.onRef?.(state)
  }, [])

  // 获取表格配置与数据
  useEffect(() => {
    const { formCode, parentTableId: Parameter1 } = props
    const { OrgId } = appStore.current.headers

    /**
     * formCode 子表传过来的是 PF001===record.ID（用于唯一标识子表，因为子表和父表是同一个 formCode）
     * 因此通过 split('===')[0] 取到正确的 formCode
     */
    const loadDataKey = formCode.split('===')[0]

    let tableConfig: any = undefined
    $api['table/getTableConfig'](
      { OrgId, Key: loadDataKey, ParentTableId: Parameter1 },
      { noErrorToast: true }
    ).then((res: any) => {
      // 兼容数组类型的返回，因为列表表单展开的子表可能存在多个子表的情况
      tableConfig = res?.[0] || res
    }).catch(() => {
      return getTablesConfig_v1(loadDataKey, { Parameter1 }).then((tables: any[]) => {
        tableConfig = tables?.[0]
      })
    }).finally(() => {
      if (tableConfig) {
        const funcs = searchStore.search.get(props.formCode)?.setSearchConfig(tableConfig)
        // setSearchConfig 需要先取到本地记忆存储的内容，所以这边使用 Promise
        Promise.all([funcs]).finally(() => {
          state.setTableConfig(tableConfig)
          state.dynamicJs[$consts['LIFECYCLE/LIST_PAGE_CONFIG_LOADED']]?.(state)
          $evt.emit($consts['LIFECYCLE/LIST_PAGE_CONFIG_LOADED'], { formCode: props.formCode, data: tableConfig })
          !props.preventAutoData && state.getTableData()
        })
      }
    })
  }, [props.formCode])

  // 响应分页数据变化，获取表格数据
  useEffect(() => reaction(
    () => state.pageOption,
    () => state.getTableData()
  ), [])

  // 同步 props.selectedKeys
  useEffect(() => {
    const { selectedKeys = [] } = props
    !isEqual(toJS(state.selectedKeys), selectedKeys) && state.setSelectedKeys(selectedKeys)
  }, [props.selectedKeys])

  // table 单选、多选
  const [rowSelection, setRowSelection] = useState<any>(undefined)
  useEffect(() => autorun(() => {
    const { radioEnable, checkboxEnable } = props
    if ((radioEnable || checkboxEnable) && state.tableData.dataSource.length) {
      setRowSelection({
        fixed: 'left',
        type: radioEnable ? 'radio' : checkboxEnable ? 'checkbox' : '',
        columnWidth: 32,
        selectedRowKeys: state.selectedKeys,
        // IS_SYSTEM 字段控制选择框是否可选择
        getCheckboxProps: (record: any) => ({
          disabled: record.IS_SYSTEM
        }),
        // 单/多选
        onSelect: (...args: any[]) => {
          const [record, selected] = args
          let newKeys = [record.ID]
          if (checkboxEnable) {
            if (selected) {
              newKeys = uniq(newKeys.concat(state.selectedKeys))
            } else {
              newKeys = state.selectedKeys.filter((key: string) => key !== newKeys[0])
            }
          }
          state.setSelectedKeys(newKeys)
          props.onSelectChange?.(newKeys)
        },
        // 全选
        onSelectAll: (...args: any[]) => {
          let [selected, selectedRows, changeRows] = args
          // 这两货竟然会有 undefined 的 item 存在
          selectedRows = selectedRows.filter((row: any) => !!row)
          changeRows = changeRows.filter((row: any) => !!row)
          /**
           * 全选时，selectedRows 肯定是全选的数据
           * 取消全选时，changeRows 肯定是全选的数据
           */
          let newKeys = selected ? selectedRows.map((row: any) => row.ID) : changeRows.map((row: any) => row.ID)
          if (selected) {
            newKeys = uniq(newKeys.concat(state.selectedKeys))
          } else {
            newKeys = state.selectedKeys.filter((key: string) => newKeys.indexOf(key) === -1)
          }
          state.setSelectedKeys(newKeys)
          props.onSelectChange?.(newKeys)
        }
      })
    }
  }), [props.radioEnable, props.checkboxEnable])

  // 行样式类
  const rowClassName = useCallback((record: any, index: number) => {
    // 添加删除的行样式
    return record?.IS_DELETED ? 'list-table__deleted-row' : ''
  }, [])

  // 单击行弹出编辑表单
  const [rowEditComponent, setRowEditComponent] = useState<any>(null)

  // 单击选中的行数据
  const handleRenderRow = useCallback((record: any) => {
    return {
      onClick: () => {
        const row = state.tableData.dataSource.find((item: any) => item.ID === record.ID)
        state.setSelectedRow(row)
        // setRowEditComponent(React.cloneElement(getRowEditBtn(state.tableConfig, record), { key: uuid() }))
        setRowEditComponent(null)
      }
    }
  }, [])

  // table 展开
  const [expandable, setExpandable] = useState<any>()
  useEffect(() => reaction(
    () => state.tableConfig.HasChildren,
    (hasChildren: boolean) => setExpandable(hasChildren ? {
      columnWidth: 32,
      rowExpandable: () => true,
      expandedRowRender: (record: any) => (
        <ListTableSubTable
          parentTableId={state.tableConfig.PageTable?.ID}
          record={record}
          formCode={props.formCode}
          filterGroup={state.getFilterGroup()}
        />
      )
    } : undefined)
  ), [])

  // list-table 固定列/表头 滚动
  const wrapEl = useRef(null)
  const scroll = useTableScroll(wrapEl, {
    validScroll: state.tableData.dataSource.length,
    ...(formProps?.formData?.useScroll || props.useScroll)
  })

  // 后端可配置的 height
  const style = useMemo(() => {
    const width = state.tableConfig.PageTable?.WIDTH
    const height = state.tableConfig.PageTable?.HEIGTH
    const configStyle = {}
    width && width !== '0' && set(configStyle, 'width', width)
    height && height !== '0' && set(configStyle, 'height', height)
    return Object.assign({}, props.style, configStyle)
  }, [props.style, state.tableConfig.PageTable?.HEIGTH])

  // loading
  const [paths, setPaths] = useState([api.paths['table/getTableConfig']])
  useEffect(() => reaction(
    () => state.tableConfig.PageTable?.API_URL,
    path => setPaths(prev => prev.concat(path))
  ), [])
  const loading = useIsLoading(paths)
  return (
    <ListTableContext.Provider value={state}>
      <div ref={wrapEl} className={`list-table ${state.uniqClass} ${props.className}`} style={style}>
        {
          state.nativeTable
            ? <ListTableNative />
            : state.cardList
              ? props.cardRender?.() ?? (
                <ListTableCard
                  checkboxEnable={props.checkboxEnable}
                  singlePageTable={props.singlePageTable}
                  onSelect={rowSelection?.onSelect}
                />
              )
              : (
                <Table
                  rowKey='ID'
                  size='middle'
                  loading={{ delay: 200, spinning: loading }}
                  columns={state.tableData.columns}
                  dataSource={state.tableData.dataSource}
                  pagination={
                    props.singlePageTable
                      || listProps.current?.formData?.__EDIT_V1__
                      || listProps.current?.formData?.__EDIT_V2__
                      ? false : state.paginationOption
                  }
                  rowSelection={rowSelection}
                  rowClassName={rowClassName}
                  expandable={expandable}
                  onRow={handleRenderRow}
                  onChange={(page, filter, sorter) => state.setPagination(page, filter, sorter)}
                  scroll={scroll}
                  footer={state.tableConfig.Nav_Form?.REMARK ? () => <ListTableFooter /> : undefined}
                  {...listProps.current.formData?.tableProps}
                  {...props.tableProps}
                />
              )
        }
      </div>
      {/** 单击行弹出编辑表单 */}
      {rowEditComponent}
    </ListTableContext.Provider>
  )
}

export { ListTableContext }
export default observer(ListTable)